/**
 * Which shape the shell's `<main>` takes for the route being rendered — the
 * seam `FE-13.x` needs from `FE-04.11`'s shell.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SHELL NEEDED A SECOND SHAPE AT ALL
 * ---------------------------------------------------------------------------
 * `FE-04.11` shipped one: `mx-auto max-w-4xl px-6 py-10`, which is right for
 * every reading surface this product has — a settings page, a boards landing,
 * the status page. It is wrong for exactly one thing, and that thing is the
 * subject of this chunk: `FR-VIEW-010` AC1 requires the board header to stay
 * fixed while the list strip scrolls horizontally beneath it, and a centred
 * column with symmetric padding cannot express that. A board face inside
 * `max-w-4xl` would either overflow the column (two horizontal scrollbars, one
 * of them the page's) or be capped at 896px on a 1440px screen, which spends a
 * third of `FE-04.12`'s density budget on margins.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS ROUTE `staticData` AND NOT A PROP, A CONTEXT OR A PATH TEST
 * ---------------------------------------------------------------------------
 * `__root.tsx` renders `<main>` and the route renders inside it, so the route
 * cannot pass the decision upward as a prop — by the time its component runs,
 * the element is already in the tree. The three alternatives and why not:
 *
 *  - **A path test in the shell** (`pathname.startsWith('/b/')`) puts knowledge
 *    of one feature's URL in the file that must not know about features, and
 *    goes stale silently the day a second full-bleed surface ships.
 *  - **A context the route sets in an effect** paints the centred layout first
 *    and then relayouts, which is a visible jump on every board open.
 *  - **A second root route** (a pathless layout route wrapping the board) is
 *    the framework-idiomatic answer and is genuinely better once there are
 *    several full-bleed surfaces; it costs a route file and a second copy of
 *    the skip link and the live region today, for one route. Revisit when the
 *    second one arrives — `FE-11.x` and `FE-12.x` render *into* this route
 *    rather than beside it, so that is not imminent.
 *
 * `staticData` is declared on the route, read during matching, and available
 * before the first paint. It is also inert: a route that says nothing gets the
 * centred layout, so every existing route keeps the shape it shipped with.
 */

/**
 * `'centred'` — the reading column `FE-04.11` shipped, and the default for
 * every route that does not ask.
 *
 * `'full-bleed'` — the viewport is the surface: no page scroll at all, the
 * header pinned, and the route owning every pixel below it. A route choosing
 * this **must** manage its own overflow, because the shell no longer will.
 *
 * `'embedded'` — a product surface rendered inside an external iframe. The
 * route gets the full viewport and the root shell suppresses navigation
 * chrome, while keeping the singleton live region and route boundary shape.
 *
 * `'wide'` — the centred column at the header's own width (`max-w-6xl`, the
 * same cap `Header.tsx` puts on its row) rather than the reading column's
 * `max-w-4xl`. For a page that lays out two columns: `/` sets its boards
 * beside a Home column, and inside 896px the boards column was left about
 * 464px — two board tiles abreast on a 1440px screen. Still a page that
 * scrolls; nothing about overflow changes.
 */
export type ShellLayout = 'centred' | 'wide' | 'full-bleed' | 'embedded';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /**
     * Declared as `staticData: { layout: 'full-bleed' }` on the route.
     * Optional: absent means `'centred'`.
     */
    layout?: ShellLayout;
  }
}

/**
 * The two class pairs, kept together because they are one decision.
 *
 * ⚠ `root` and `main` are not independently choosable. `full-bleed`'s `main`
 * is `flex-1 min-h-0`, which only means anything inside a flex column of a
 * fixed height — so its `root` must be `flex h-screen flex-col`. Splitting
 * them into two lookups is how a later edit gets a board face that scrolls the
 * page as well as the strip.
 *
 * `min-h-0` is the load-bearing half and it is invisible: a flex child's
 * default `min-height: auto` refuses to shrink below its content, so a column
 * of 200 cards would push `<main>` past the viewport and the page would scroll
 * — the exact failure `overflow-hidden` on the root looks like it prevents and
 * does not.
 */
const SHELL_CLASSES: Readonly<Record<ShellLayout, { root: string; main: string }>> = {
  centred: {
    root: 'min-h-screen',
    main: 'mx-auto max-w-4xl px-6 py-10',
  },
  wide: {
    root: 'min-h-screen',
    main: 'mx-auto max-w-6xl px-6 py-10',
  },
  'full-bleed': {
    root: 'flex h-screen flex-col overflow-hidden',
    main: 'flex min-h-0 flex-1 flex-col',
  },
  embedded: {
    root: 'min-h-screen bg-surface',
    main: 'min-h-screen',
  },
};

/** What one route match contributes, structurally — see `shellLayoutOf`. */
export interface ShellLayoutMatch {
  readonly staticData?: { readonly layout?: ShellLayout } | undefined;
}

/**
 * The layout the current match chain asks for.
 *
 * **The deepest match wins**, and the walk is from the end for that reason: the
 * root route is in every chain and declares nothing, so a first-match rule
 * would answer `'centred'` for every route in the product. A parent that
 * declares a layout is inherited by a child that does not, which is what makes
 * a future layout route a drop-in replacement for this function.
 */
export function shellLayoutOf(matches: readonly ShellLayoutMatch[]): ShellLayout {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const layout = matches[index]?.staticData?.layout;
    if (layout !== undefined) return layout;
  }
  return 'centred';
}

/** The classes for a layout. Returns both halves; see `SHELL_CLASSES`. */
export function shellClasses(layout: ShellLayout): { root: string; main: string } {
  return SHELL_CLASSES[layout];
}
